import type { AiAgentContinueResponse, AiChatMessage } from '../../../../data/ServerApiClient'
import type { CfoAnalysisActiveSession } from './CfoAnalysisProvider'

const trimStr = (value: unknown): string => String(value ?? '').trim()

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const textBlock = (value: unknown): string => {
  const text = trimStr(value)
  if (!text) return ''
  return `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`
}

const listItems = (items: unknown[] | undefined | null): string => {
  const rendered = (items || []).map(trimStr).filter(Boolean).map((item) => `<li>${escapeHtml(item)}</li>`)
  return rendered.length ? `<ul>${rendered.join('')}</ul>` : ''
}

export const cfoAnalysisChartSummary = (chart: any): string => {
  if (!chart || typeof chart !== 'object') return ''
  const title = trimStr(chart.title)
  const rows = Array.isArray(chart.rows)
    ? chart.rows.slice(0, 8).map((row: any) => Object.entries(row || {}).map(([k, v]) => `${k}: ${v}`).join(', '))
    : []
  const items = Array.isArray(chart.items)
    ? chart.items.slice(0, 8).map((item: any) => `${trimStr(item?.label) || 'Item'}: ${item?.value ?? ''}${trimStr(item?.note) ? ` (${trimStr(item.note)})` : ''}`)
    : []
  return [title, ...rows, ...items].filter(Boolean).join('\n')
}

const renderChartSummary = (chart: any): string => {
  const summary = cfoAnalysisChartSummary(chart)
  return summary ? `<pre>${escapeHtml(summary)}</pre>` : ''
}

const renderMetadata = (session: CfoAnalysisActiveSession): string => {
  const entries: Array<[string, unknown]> = [
    ['Period', session.monthKey],
    ['Page', session.context.pageId],
    ['Source', session.context.sourceComponent],
  ]
  const html = entries
    .map(([label, value]) => [label, trimStr(value)] as const)
    .filter(([, value]) => value)
    .map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`)
    .join('')
  return html ? `<dl>${html}</dl>` : ''
}

const renderStartingEvidence = (session: CfoAnalysisActiveSession): string => {
  const visual = session.visual
  if (!visual) return ''
  const body =
    visual.kind === 'value'
      ? [
          `<p><strong>${escapeHtml(visual.label)}</strong>: ${escapeHtml(visual.display)}</p>`,
          textBlock(visual.sub),
        ].join('')
      : visual.kind === 'ratio_health'
        ? [
            `<p><strong>${escapeHtml(visual.label)}</strong>: ${escapeHtml(visual.display)}</p>`,
            textBlock(visual.interpretation),
          ].join('')
        : [textBlock(visual.caption), renderChartSummary(visual.chart)].join('')
  return `<section><h2>Starting evidence</h2>${body}</section>`
}

const renderInstantCard = (session: CfoAnalysisActiveSession): string => {
  const card = session.instantCard
  if (!card) return ''
  const facts = (card.supportingFacts || [])
    .slice(0, 8)
    .map((item) => `<li><strong>${escapeHtml(item.label)}</strong>: ${escapeHtml(item.value)}</li>`)
    .join('')
  const actions = (card.actions || [])
    .slice(0, 6)
    .map((item) => `<li>${escapeHtml(item.label)}</li>`)
    .join('')
  return [
    '<section>',
    '<h2>Instant read</h2>',
    `<h3>${escapeHtml(card.title)}</h3>`,
    card.subtitle ? `<p>${escapeHtml(card.subtitle)}</p>` : '',
    `<p><strong>${escapeHtml(card.primaryMetric.label)}</strong>: ${escapeHtml(card.primaryMetric.value)}</p>`,
    textBlock(card.deterministicRead),
    facts ? `<ul>${facts}</ul>` : '',
    actions ? `<h3>Next steps</h3><ul>${actions}</ul>` : '',
    '</section>',
  ].join('')
}

const renderTranscript = (messages: AiChatMessage[]): string => {
  const turns = messages
    .map((message, idx) => {
      const label = message.role === 'user' ? 'User' : 'Analyst'
      const content = textBlock(message.content)
      const chart = renderChartSummary(message.metadata?.chart)
      if (!content && !chart) return ''
      return `<li><strong>${idx + 1}. ${label}</strong>${content}${chart}</li>`
    })
    .filter(Boolean)
  return turns.length ? `<section><h2>Transcript</h2><ol>${turns.join('')}</ol></section>` : ''
}

const titledList = (title: string, items: unknown): string => {
  const list = listItems(Array.isArray(items) ? items : [])
  return list ? `<h3>${escapeHtml(title)}</h3>${list}` : ''
}

const renderContextUsed = (response?: Pick<AiAgentContinueResponse, 'financial_work_receipt'> | null): string => {
  const context = response?.financial_work_receipt?.context_used
  if (!context) return ''
  const entries: Array<[string, unknown]> = [
    ['Company', context.company_name],
    ['Actuals available', context.latest_available_period || context.effective_through],
    ['Reconciled through', context.latest_reconciled_period],
    ['Source health', trimStr(context.source_health).replace(/_/g, ' ')],
    ['Evidence references', context.evidence_ref_count],
  ]
  const metadata = entries
    .map(([label, value]) => [label, trimStr(value)] as const)
    .filter(([, value]) => value)
    .map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`)
    .join('')
  return [
    '<section><h2>Context used</h2>',
    metadata ? `<dl>${metadata}</dl>` : '',
    titledList('Relevant statements', context.relevant_statement_ids),
    titledList('Relevant metrics', context.relevant_metric_ids),
    context.financial_period_disclosure ? textBlock(context.financial_period_disclosure) : '',
    titledList('Context conflicts requiring review', context.context_conflicts),
    '</section>',
  ].join('')
}

const renderFinalAnswer = (response?: Pick<AiAgentContinueResponse, 'answer' | 'financial_work_receipt'> | null): string => {
  const summary = response?.financial_work_receipt?.completion?.completion_summary
  if (summary) {
    const body = [
      trimStr(summary.headline) ? `<h3>${escapeHtml(summary.headline)}</h3>` : '',
      titledList('What I found', summary.what_i_found),
      titledList('What matters', summary.what_matters),
      titledList('What I changed', summary.what_i_changed),
      titledList('Verification performed', summary.verification_performed),
      titledList('Exceptions and unresolved items', summary.exceptions_and_unresolved_items),
      titledList('Sheets and ranges changed', summary.sheets_and_ranges_changed),
    ].join('')
    return `<section><h2>Validated completion</h2>${body}</section>`
  }
  const answer = response?.answer
  if (!answer) return ''
  const body = [
    trimStr(answer.headline) ? `<h3>${escapeHtml(answer.headline)}</h3>` : '',
    textBlock(answer.executive_summary),
    listItems(answer.detailed_narrative),
    listItems((answer.caveats || []).map((item) => `Caveat: ${item}`)),
  ].join('')
  return body ? `<section><h2>Final answer</h2>${body}</section>` : ''
}

export function buildCfoAnalysisSessionHtml({
  session,
  visibleMessages,
  response,
}: {
  session: CfoAnalysisActiveSession
  visibleMessages: AiChatMessage[]
  response?: Pick<AiAgentContinueResponse, 'answer' | 'financial_work_receipt'> | null
}): string {
  const title = trimStr(session.context.metricLabel) || trimStr(session.label) || 'CFO analysis'
  const article = [
    `<article class="cfo-analysis-session" data-session-id="${escapeHtml(session.id)}">`,
    `<header><h1>${escapeHtml(title)}</h1>${renderMetadata(session)}</header>`,
    renderInstantCard(session),
    renderStartingEvidence(session),
    renderTranscript(visibleMessages),
    renderFinalAnswer(response),
    renderContextUsed(response),
    '</article>',
  ].join('')

  return [
    '<!doctype html>',
    '<html>',
    '<head>',
    '<meta charset="utf-8">',
    `<title>${escapeHtml(title)}</title>`,
    '<style>',
    'body{font-family:Inter,Arial,sans-serif;color:#111827;line-height:1.5;margin:32px;}',
    'article{max-width:860px;margin:0 auto;}',
    'h1{font-size:28px;margin:0 0 12px;}',
    'h2{font-size:18px;margin:28px 0 10px;}',
    'h3{font-size:16px;margin:0 0 8px;}',
    'dl{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:8px 16px;margin:0 0 16px;}',
    'dt{font-size:11px;text-transform:uppercase;color:#6b7280;}',
    'dd{margin:0;font-weight:600;}',
    'ol,ul{padding-left:22px;}',
    'li{margin:0 0 12px;}',
    'pre{white-space:pre-wrap;background:#f6f7f9;border:1px solid #e5e7eb;border-radius:6px;padding:12px;}',
    '</style>',
    '</head>',
    `<body>${article}</body>`,
    '</html>',
  ].join('')
}
